/*----------------------------------
- DOMAINS CARD VISUALS (decorative only)
- The supplied sample domains keep their reference colour. Remaining domains use a stable hash of
- the domain string.
- Presentation mechanics only: this encodes no demand, quality, or hotness signal.
----------------------------------*/

/**
 * @docs docs/features/website-domains-hubs
 *
 * The row's glyph colour (the kit `Glyph`'s `--ud-seed`). The shipped tile had nine decorative hex
 * colours; the design system draws the glyph in tokens, so each of the nine maps to one token in the
 * same order (the mock's `SEEDS`, website-domains.html), and a name keeps one colour on every list,
 * in the table, the phone card and the drawer head alike.
 */

/*----------------------------------
- CONSTANTS
----------------------------------*/

/**
 * The nine seed tokens, in the order of the retired hex palette (orange, blue, red, pink, purple,
 * violet, yellow, green, teal), so the hash below lands every name on the colour family it had.
 */
const CARD_SEED_TOKENS = [
    'var(--ud-alert)',
    'var(--ud-viz-2)',
    'var(--ud-alert-ink)',
    'var(--ud-auction)',
    'var(--ud-premium)',
    'var(--ud-premium-ink)',
    'var(--ud-viz-1)',
    'var(--ud-positive)',
    'var(--ud-dormant)',
] as const;

export type CardSeedToken = (typeof CARD_SEED_TOKENS)[number];

/** The sample domains' reference colours (an index into `CARD_SEED_TOKENS`), as the mockup drew them. */
const REFERENCE_CARD_SEEDS: Readonly<Record<string, number>> = {
    'summit.co': 0,
    'atlas.ai': 7,
    'nimbus.io': 1,
    'radius.co': 6,
    'orbit.app': 8,
    'lumen.dev': 4,
    'vertex.ai': 3,
    'harbor.co': 0,
};

/*----------------------------------
- HELPERS
----------------------------------*/

const stableHash = (value: string): number => {
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) {
        hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
    }
    return hash;
};

/*----------------------------------
- PUBLIC API
----------------------------------*/

/** The glyph's seed token for a domain: its reference colour, else the same hash bucket the hex palette used. */
export const pickCardSeed = (domain: string): CardSeedToken => {
    const reference = REFERENCE_CARD_SEEDS[domain.trim().toLowerCase()];
    if (reference !== undefined) return CARD_SEED_TOKENS[reference];
    return CARD_SEED_TOKENS[(stableHash(domain) >>> 4) % CARD_SEED_TOKENS.length];
};
